import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { withStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  Table,
  TableBody
} from '@material-ui/core';

import TableHeader from './table_header.js.jsx';
import TableRow from './table_row.js.jsx';

const styles = theme => ({
  root: {},
  content: {
    padding: 0
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  actions: {
    justifyContent: 'flex-end'
  }
});

/**
 * On table row clicked
 * @callback TableView~onClickRow
 * @param {Object} item Clicked item
 * @param {number} index Index of clicked item
 */

/**
 * On item changed handler
 * @callback TableView~onChangeItem
 * @param {Object} item New value of changed item
 * @param {number} index Index of changed item
 * @param {TableCellDescription} changedColumn Last changed column
 */

/**
 * On change ordering callback
 * @callback TableView~onChangeSort
 * @param {string} orderColumn New ordering column
 * @param {string} orderType New ordering direction. Available values: "asc", "desc"
 */

 /**
  * On all row selection changed
  * @callback TableView~onChangeAllSelection
  * @param {boolean} value Selected or deselected items
  */

/**
  * On change selection of row
  * @callback TableView~onChangeSelection
  * @param {Object} item Selected item
  * @param {boolean} value Is select or deselect item right now
  */

/**
 * Object, described current filter data, applied to items list
 * @typedef {Object<string, any>} TableView~filterData
 * @property {string} [orderColumn] Current column for ordering
 * @property {string} [orderType] Order direction. Available values: "asc", "desc"
 * @example
 * const filterData = {
 *  orderColumn: "created_at",
 *  orderType: "desc",
 *  status: ["active", "upcoming"]
 * }
 */

/**
 * Component for render tables for admin
 * @name TableView
 * @param {object} props React component properties
 * @param {Array.<TableCellDescription>} props.columns Columns of table
 * @param {Array.<Object>} props.items Items to display. All items must have property "key", unique identifier of item
 * @param {TableView~filterData} [props.filterData] Current filter, applied to items list
 * @param {TableView~onChangeSort} [props.onChangeSort] On change sort column and/or direction
 * @param {TableView~onClickRow} [props.onClickRow] On row click handler
 * @param {TableView~onChangeItem} [props.onChangeItem] On item changed handler
 * @param {TableView~onChangeAllSelection} [props.onChangeAllSelection] On change all selection
 * @param {TableView~onChangeSelection} [props.onChangeSelection] On change selection or row
 * @param {HTMLElement} [props.header] Special component for rendering table header instead {@link TableHeader}
 * @param {boolean} [props.allowSelection = false] Allow selection rows
 * @param {boolean} [props.allSelected = false] Is all items selected
 * @example
 * const filterData = {
 *  orderColumn: "created_at",
 *  orderType: "desc",
 *  status: ["active", "upcoming"]
 * }
 * const columns = this.columns;
 * return (
 *  <TableView columns = { columns }
 *              filterData = { filterData }
 *              items = { [{...}, {...}] }
 *              onChangeSort = { (orderColumn, orderType) => {
 *                // Some actions
 *              } } />
 * )
*/
class TableView extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      
    }

    this.onChangeAllSelection = this.onChangeAllSelection.bind(this);
    this.onChangeSelection = this.onChangeSelection.bind(this);
    this.onChangeSort = this.onChangeSort.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    return {
      filterData: { ...props.filterData },
      columns: [...props.columns],
      allSelected: props.allSelected
    }
  }

  onChangeAllSelection(value) {

  }

  onChangeSelection(item, value) {

  }

  onChangeSort(orderColumn, orderType) {
    const { onChangeSort } = this.props;
    if(onChangeSort) {
      onChangeSort(orderColumn, orderType);
    }
  }

  onChangeItem = (item, index, changedColumn) => {
    const { onChangeItem } = this.props;
    if(onChangeItem) {
      onChangeItem(item, index, changedColumn);
    }
  }

  render() {
    const { allowSelection, classes, className, onClickRow } = this.props;
    const { header, children, items } = this.props;
    
    const { columns, allSelected, filterData } = this.state;

    if(columns.length === 0) {
      return <React.Fragment />
    }
    
    return <Card className = { clsx(classes.root, className) } >
      <CardContent className = { classes.content } >
        <PerfectScrollbar>
          <div className = { classes.inner } >
            <Table>
              {
                header
              }
              {
                !header &&
                <TableHeader columns = { columns } 
                             allSelected = { allSelected }
                             filterData = { filterData }
                             allowSelection = { allowSelection }
                             onChangeAllSelection = { this.onChangeAllSelection }
                             onChangeSort = { this.onChangeSort }  />
              }
              <TableBody>
                {
                  children
                }
                {
                  !children &&
                  <React.Fragment>
                    {
                      items.map((item, index) => {
                        return <TableRow key = { item.key }
                                         columns = { columns } 
                                         allowSelection = { allowSelection }
                                         item = { item }
                                         items = { items }
                                         onChangeSelection = { (value) => this.onChangeAllSelection(item, value) }
                                         onChangeItem = { (item, column) => this.onChangeItem(item, index, column ) }
                                         hoverable = { Boolean(item.itemLink || onClickRow) }
                                         onClick = { (item) => {
                                           if(onClickRow)
                                            onClickRow(item, index)
                                         } } />
                      })
                    }
                  </React.Fragment>
                }
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </CardContent>
    </Card>
  }
}

TableView.propTypes = {
  columns: PropTypes.array.isRequired,
  items: PropTypes.array.isRequired,
  filterData: PropTypes.object,
  onChangeSort: PropTypes.func,
  onClickRow: PropTypes.func,
  onChangeItem: PropTypes.func,
  header: PropTypes.node,
  allSelected: PropTypes.bool,
  allowSelection: PropTypes.bool
}

TableView.defaultProps = {
  allSelected: false,
  allowSelection: false
}

export default withStyles(styles)(TableView);